/**
 * Layout QA Page - Dev-only visual spot check
 * Access via /layout-qa?debug=1
 * Renders key UI components for quick visual verification
 */
import ContextBar from '../../components/shared/ContextBar.jsx';
import ReportingPeriodBar from '../../components/shared/ReportingPeriodBar.jsx';
import KPICardRow from '../../components/shared/KPICardRow.jsx';
import OwnerModuleCard from '../../components/shared/OwnerModuleCard.jsx';

export default function LayoutQAPage() {
  const sampleKPIs = [
    { label: 'Reservations', value: '42' },
    { label: 'Nights', value: '128' },
    { label: 'Revenue', value: '$12,450' },
  ];

  const sampleChannels = [
    { code: 'direct', name: 'Direct', color_hex: '#3b82f6' },
    { code: 'airbnb', name: 'Airbnb', color_hex: '#ff5a5f' },
    { code: 'vrbo', name: 'VRBO', color_hex: '#00a699' },
    { code: 'booking', name: 'Booking.com', color_hex: '#003580' },
  ];

  return (
    <div className="owner-dashboard-container">
      <ContextBar 
        title="Layout QA" 
        subtitle="Visual spot check for key components"
      >
        <ReportingPeriodBar />
      </ContextBar>

      {/* KPI Row */}
      <KPICardRow kpis={sampleKPIs} />

      {/* Calendar Toolbar Sample */}
      <OwnerModuleCard title="Calendar Toolbar">
        <div className="calendar-toolbar" data-testid="calendar-toolbar" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--spacing-3)',
          marginBottom: 'var(--spacing-4)',
          paddingBottom: 'var(--spacing-3)',
          borderBottom: '1px solid var(--color-border-light)',
        }}>
          <div className="toolbar-left" style={{ flex: '0 0 auto', whiteSpace: 'nowrap' }}>
            <h3 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semibold)', margin: 0 }}>January 2026</h3>
          </div>
          <div className="calendar-toolbar-legend" data-testid="calendar-legend" style={{
            display: 'flex',
            flex: '1 1 auto',
            minWidth: 0,
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'var(--spacing-4)',
            whiteSpace: 'nowrap',
          }}>
            {sampleChannels.map((channel, idx) => (
              <div key={channel.code} className="legend-item" style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
              }}>
                <div style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  backgroundColor: channel.color_hex,
                  flexShrink: 0,
                }} />
                <span style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-secondary)',
                  fontWeight: 'var(--font-weight-medium)',
                }}>{channel.name}</span>
                {idx < sampleChannels.length - 1 && (
                  <span style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-tertiary)',
                    margin: '0 var(--spacing-1)',
                  }}>•</span>
                )}
              </div>
            ))}
          </div>
          <div className="toolbar-nav" style={{
            display: 'flex',
            flex: '0 0 auto',
            gap: 'var(--spacing-2)',
            alignItems: 'center',
          }}>
            <button style={{
              padding: 'var(--spacing-1) var(--spacing-3)',
              backgroundColor: 'var(--color-bg-primary)',
              border: '1px solid var(--color-border-medium)',
              borderRadius: 'var(--radius-sm)',
              fontSize: 'var(--font-size-sm)',
              cursor: 'pointer',
            }}>←</button>
            <button style={{
              padding: 'var(--spacing-1) var(--spacing-3)',
              backgroundColor: 'var(--color-bg-primary)',
              border: '1px solid var(--color-border-medium)',
              borderRadius: 'var(--radius-sm)',
              fontSize: 'var(--font-size-sm)',
              cursor: 'pointer',
            }}>Today</button>
            <button style={{
              padding: 'var(--spacing-1) var(--spacing-3)',
              backgroundColor: 'var(--color-bg-primary)',
              border: '1px solid var(--color-border-medium)',
              borderRadius: 'var(--radius-sm)',
              fontSize: 'var(--font-size-sm)',
              cursor: 'pointer',
            }}>→</button>
          </div>
        </div>
      </OwnerModuleCard>

      {/* Upcoming Events Header Sample */}
      <OwnerModuleCard 
        title="Upcoming Events"
        headerActions={
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-3)',
          }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '4px',
              borderRadius: '999px',
              backgroundColor: 'var(--color-bg-tertiary)',
              border: '1px solid rgba(15, 23, 42, 0.10)',
            }}>
              <button style={{
                appearance: 'none',
                border: '0',
                background: 'var(--color-brand-primary)',
                height: '32px',
                padding: '0 12px',
                borderRadius: '999px',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semibold)',
                color: '#ffffff',
                cursor: 'pointer',
              }}>All</button>
              <button style={{
                appearance: 'none',
                border: '0',
                background: 'transparent',
                height: '32px',
                padding: '0 12px',
                borderRadius: '999px',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--color-text-secondary)',
                cursor: 'pointer',
              }}>Check-ins</button>
              <button style={{
                appearance: 'none',
                border: '0',
                background: 'transparent',
                height: '32px',
                padding: '0 12px',
                borderRadius: '999px',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--color-text-secondary)',
                cursor: 'pointer',
              }}>Check-outs</button>
            </div>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '28px',
              padding: '0 10px',
              borderRadius: '999px',
              fontSize: '12px',
              fontWeight: 'var(--font-weight-semibold)',
              color: 'var(--color-status-warning-text)',
              backgroundColor: 'var(--color-status-warning-bg)',
            }}>4</div>
          </div>
        }
      >
        <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
          Sample event list would appear here
        </p>
      </OwnerModuleCard>

      {/* Table Header Sample */}
      <OwnerModuleCard title="Table Sample">
        <div style={{
          overflowX: 'auto',
        }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
          }}>
            <thead>
              <tr>
                <th style={{
                  padding: 'var(--spacing-3)',
                  textAlign: 'left',
                  backgroundColor: 'var(--color-bg-tertiary)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--color-text-primary)',
                  fontSize: 'var(--font-size-sm)',
                  borderBottom: '2px solid var(--color-border-medium)',
                }}>Guest</th>
                <th style={{
                  padding: 'var(--spacing-3)',
                  textAlign: 'left',
                  backgroundColor: 'var(--color-bg-tertiary)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--color-text-primary)',
                  fontSize: 'var(--font-size-sm)',
                  borderBottom: '2px solid var(--color-border-medium)',
                }}>Check-in</th>
                <th style={{
                  padding: 'var(--spacing-3)',
                  textAlign: 'left',
                  backgroundColor: 'var(--color-bg-tertiary)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--color-text-primary)',
                  fontSize: 'var(--font-size-sm)',
                  borderBottom: '2px solid var(--color-border-medium)',
                }}>Nights</th>
                <th style={{
                  padding: 'var(--spacing-3)',
                  textAlign: 'left',
                  backgroundColor: 'var(--color-bg-tertiary)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--color-text-primary)',
                  fontSize: 'var(--font-size-sm)',
                  borderBottom: '2px solid var(--color-border-medium)',
                }}>Revenue</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: '1px solid var(--color-border-light)' }}>
                <td style={{ padding: 'var(--spacing-3)', fontSize: 'var(--font-size-sm)' }}>Sample Guest</td>
                <td style={{ padding: 'var(--spacing-3)', fontSize: 'var(--font-size-sm)' }}>Jan 15, 2026</td>
                <td style={{ padding: 'var(--spacing-3)', fontSize: 'var(--font-size-sm)' }}>3</td>
                <td style={{ padding: 'var(--spacing-3)', fontSize: 'var(--font-size-sm)' }}>$450</td>
              </tr>
            </tbody>
          </table>
        </div>
      </OwnerModuleCard>
    </div>
  );
}
